import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ganttAPI } from '../../services/inovacaoAPI';

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function iso(d) { return d.toISOString().slice(0, 10); }

export default function GanttConsolidado() {
  const [dados, setDados] = useState(null);
  const [inicio, setInicio] = useState(iso(addDays(new Date(), -14)));
  const [fim, setFim] = useState(iso(addDays(new Date(), 90)));

  useEffect(() => {
    ganttAPI.consolidado({ inicio, fim })
      .then(setDados)
      .catch((e) => toast.error(e.response?.data?.detail || 'Erro'));
  }, [inicio, fim]);

  if (!dados) return <div style={{ padding: 32 }}>Carregando…</div>;

  const inicioD = new Date(inicio);
  const fimD = new Date(fim);
  const intervaloValido = fimD > inicioD;
  const totalDias = Math.max(1, Math.round((fimD - inicioD) / 86400000));
  const hoje = new Date();
  const hojePct = (hoje >= inicioD && hoje <= fimD) ? ((hoje - inicioD) / 86400000) / totalDias * 100 : null;
  const nTicks = Math.min(12, Math.max(4, Math.round(totalDias / 14)));
  const ticks = Array.from({ length: nTicks + 1 }, (_, i) => {
    const d = addDays(inicioD, Math.round((i * totalDias) / nTicks));
    return { pct: (i / nTicks) * 100, label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) };
  });

  function barra(faseInicio, faseFim, cor, emConflito) {
    const fi = new Date(faseInicio);
    const ff = new Date(faseFim);
    const left = Math.max(0, (fi - inicioD) / 86400000) / totalDias * 100;
    const width = Math.max(1, (ff - fi) / 86400000 + 1) / totalDias * 100;
    return { left: `${left}%`, width: `${width}%`,
             background: emConflito ? '#e74c3c' : cor,
             border: emConflito ? '2px solid #c0392b' : 'none' };
  }

  return (
    <div style={{ padding: 32 }}>
      <h1>Gantt consolidado</h1>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <label>Início: <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></label>
        <label>Fim: <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} /></label>
      </div>

      {!intervaloValido && (
        <div style={{ background: '#fdebd0', color: '#b9770e', padding: 10, borderRadius: 6, marginBottom: 16 }}>
          A data de fim precisa ser depois da data de início.
        </div>
      )}

      {dados.conflitos.length > 0 && (
        <div style={{ background: '#fadbd8', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          <b>{dados.conflitos.length} conflito(s) detectado(s)</b>:
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {dados.conflitos.map((c, i) => (
              <li key={i}>
                <b>{c.pessoa?.nome}</b> · {c.periodo_inicio} → {c.periodo_fim} ·
                {c.horas_alocadas}h de {c.capacidade_semana}h (<b>{c.severidade}</b>)
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        {/* Régua de datas (alinhada ao trilho das barras) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px',
                      borderBottom: '1px solid #eee', background: '#fafbfc' }}>
          <div style={{ width: 260, flexShrink: 0, fontSize: '0.7rem', color: '#95a5a6', fontWeight: 600 }}>
            Fase · responsável
          </div>
          <div style={{ flex: 1, position: 'relative', height: 16 }}>
            {ticks.map((t, i) => (
              <div key={i} style={{ position: 'absolute', left: `${t.pct}%`, top: 0, transform: 'translateX(-50%)',
                                    fontSize: '0.66rem', color: '#95a5a6', whiteSpace: 'nowrap' }}>{t.label}</div>
            ))}
            {hojePct != null && (
              <div style={{ position: 'absolute', left: `${hojePct}%`, top: 0, transform: 'translateX(-50%)',
                            fontSize: '0.6rem', fontWeight: 700, color: '#e74c3c' }}>hoje</div>
            )}
          </div>
        </div>
        {dados.projetos.map((p) => (
          <div key={p.id} style={{ borderBottom: '1px solid #eee', padding: 12 }}>
            <h3 style={{ marginBottom: 8 }}>
              <span style={{ background: p.cor_ui, width: 14, height: 14, display: 'inline-block',
                             borderRadius: 4, marginRight: 8 }}></span>
              {p.nome} <span style={{ color: '#777', fontSize: '0.85rem' }}>({p.estado})</span>
            </h3>
            {p.fases.map((f) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                <div style={{ width: 260, fontSize: '0.85rem' }}>
                  {f.eh_buffer && '🔸 '}{f.nome} <span style={{ color: '#aaa' }}>({f.responsavel?.nome || '—'})</span>
                </div>
                <div style={{ flex: 1, position: 'relative', height: 16, background: '#f5f5f5', borderRadius: 3 }}>
                  {hojePct != null && (
                    <div style={{ position: 'absolute', left: `${hojePct}%`, top: 0, bottom: 0,
                                  width: 1, background: 'rgba(231,76,60,0.45)', pointerEvents: 'none' }} />
                  )}
                  {f.data_inicio && f.data_fim && (
                    <div style={{
                      position: 'absolute', top: 0, height: 16, borderRadius: 3,
                      ...barra(f.data_inicio, f.data_fim, p.cor_ui, f.em_conflito),
                    }}
                    title={`${f.data_inicio} → ${f.data_fim} · ${f.horas_estimadas}h`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
        {!dados.projetos.length && (
          <p style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
            Nenhum projeto no período selecionado.
          </p>
        )}
      </div>
    </div>
  );
}
